import { runJupiterSwapQuoteQueryForCommonQueries } from '@/lib/collections/commonQueries';
import { USDC } from '@/lib/constants';
import { getDecimalsForMint } from '@/utils/tokenConfig';

// USDC has 6 decimals — 1 USDC = 1,000,000 base units
const USDC_DECIMALS = 6;

// Cache successful prices for 2 minutes to avoid hammering the API
interface PriceCache {
  usdPerToken: number; // USD per 1 human-readable token unit
  expiresAt: number;
}

const priceCache = new Map<string, PriceCache>();
const CACHE_TTL_MS = 2 * 60 * 1000;

// Short negative cache: when a Jupiter quote fails, remember the failure for a
// little while so we don't hammer the API on every render/poll. This is a
// *separate* map from priceCache — never stored as if it were a real price —
// so a later successful quote always takes priority and replaces it immediately
// (see the `failureCache.delete` call in getUsdPricePerToken below).
const failureCache = new Map<string, number>(); // mint -> failedUntil (ms epoch)
const FAILURE_CACHE_TTL_MS = 20 * 1000;

// A Jupiter quote for exactly 1 human unit of a cheap token (e.g. BONK, ANGEL at
// ~$0.0000038) returns only a handful of USDC base units — about 1 significant
// digit. To get a well-resolved quote we scale the notional up toward this target
// USD value (landing the USDC leg around 1e7 base units, ~7-8 significant digits)
// then divide the result back down by the same factor to recover the per-unit price.
const TARGET_NOTIONAL_USD = 10;
// If a 1-unit probe quote already clears this bar (e.g. SOL, any token priced
// >= $1), the output already carries 6+ significant digits — don't rescale and
// risk extra price impact from an inflated notional for an already-expensive token.
const MIN_RESOLVED_PROBE_USD = 1;
// Hard cap on the scale factor so an extremely thin/cheap token never triggers an
// absurdly large notional quote (which would eat price impact and also risk
// unreasonable swap sizes against thin liquidity).
const MAX_SCALE_FACTOR = 1_000_000;

/**
 * USDC is the quote denominator for every other lookup here, so round-tripping
 * a USDC->USDC Jupiter quote is meaningless (and often fails/returns garbage).
 * Treat it as a known, static $1.00 instead of a fallback guess.
 */
function getStaticUsdPrice(tokenMint: string): number | null {
  return tokenMint === USDC ? 1.0 : null;
}

/**
 * Get the USD price per 1 human-readable unit of a token.
 * Uses a Jupiter swap quote to USDC. Returns null (never a hardcoded guess) when
 * no real price can be determined — callers must treat null as "unavailable", not zero.
 *
 * @param tokenMint - The mint address (use SOL constant for native SOL)
 * @returns USD value per 1 human unit (e.g. price of 1 SOL, 1 BONK, 1 SKR), or null if unavailable
 */
export async function getUsdPricePerToken(tokenMint: string): Promise<number | null> {
  const staticPrice = getStaticUsdPrice(tokenMint);
  if (staticPrice !== null) return staticPrice;

  const now = Date.now();
  const cached = priceCache.get(tokenMint);
  if (cached && cached.expiresAt > now) {
    return cached.usdPerToken;
  }

  // Respect a short negative cache so a burst of failed lookups doesn't keep
  // re-hitting Jupiter — but always let a fresh attempt through once it expires.
  const failedUntil = failureCache.get(tokenMint);
  if (failedUntil && failedUntil > now) {
    return null;
  }

  try {
    const decimals = getDecimalsForMint(tokenMint);
    // Quote for 1 human unit in base units
    const oneHumanUnitRaw = Math.pow(10, decimals);

    // Use a unique query id based on the token + timestamp bucket (2-min window)
    const timeBucket = Math.floor(now / CACHE_TTL_MS);
    const queryId = `price_${tokenMint.slice(0, 8)}_${timeBucket}`;

    // Step 1: probe with exactly 1 human unit (cheap tokens like SOL already
    // resolve well here, so this is often the only quote we need).
    const probeUsdcBaseUnits = await runJupiterSwapQuoteQueryForCommonQueries(`${queryId}_probe`, {
      inputMint: tokenMint,
      outputMint: USDC,
      amount: String(oneHumanUnitRaw),
    });
    const probeUsdValue = probeUsdcBaseUnits / Math.pow(10, USDC_DECIMALS);

    let usdValue = 0;

    if (probeUsdValue >= MIN_RESOLVED_PROBE_USD) {
      // Already well-resolved (e.g. SOL) — use it directly rather than inflating
      // the notional and eating unnecessary price impact.
      usdValue = probeUsdValue;
    } else if (probeUsdValue > 0) {
      // Thin per-unit notional (e.g. BONK, ANGEL) — the 1-unit probe only returned
      // ~1 significant digit. Derive a scale factor from the *actual* probe price
      // (never a hardcoded multiplier) that pushes the notional toward
      // TARGET_NOTIONAL_USD, re-quote at that size, then divide the result back
      // down by the same factor to recover the true per-unit price.
      const rawScale = TARGET_NOTIONAL_USD / probeUsdValue;
      const scaleFactor = Math.min(Math.max(Math.ceil(rawScale), 1), MAX_SCALE_FACTOR);

      const scaledHumanUnitsRaw = Math.round(oneHumanUnitRaw * scaleFactor);
      const scaledUsdcBaseUnits = await runJupiterSwapQuoteQueryForCommonQueries(`${queryId}_scaled`, {
        inputMint: tokenMint,
        outputMint: USDC,
        amount: String(scaledHumanUnitsRaw),
      });
      const scaledUsdValue = scaledUsdcBaseUnits / Math.pow(10, USDC_DECIMALS);
      usdValue = scaledUsdValue / scaleFactor;
    }

    if (usdValue > 0) {
      priceCache.set(tokenMint, { usdPerToken: usdValue, expiresAt: now + CACHE_TTL_MS });
      // A real price always supersedes a stale negative cache immediately.
      failureCache.delete(tokenMint);
      return usdValue;
    }
  } catch {
    // Fall through to negative cache below
  }

  failureCache.set(tokenMint, now + FAILURE_CACHE_TTL_MS);
  return null;
}

/**
 * Convert a raw token amount (in base units) to its USD value.
 * @param rawAmount - Token amount in base units (e.g. lamports for SOL)
 * @param tokenMint - The token's mint address
 * @returns USD value, or null if a price could not be determined (async, fetches price if needed)
 */
export async function rawAmountToUsd(rawAmount: number, tokenMint: string): Promise<number | null> {
  const decimals = getDecimalsForMint(tokenMint);
  const humanAmount = rawAmount / Math.pow(10, decimals);
  const pricePerToken = await getUsdPricePerToken(tokenMint);
  if (pricePerToken === null) return null;
  return humanAmount * pricePerToken;
}

/**
 * Convert a human-readable token amount to its USD value.
 * @param humanAmount - Token amount in human units (e.g. 1.5 for 1.5 SOL)
 * @param tokenMint - The token's mint address
 * @returns USD value, or null if a price could not be determined (async, fetches price if needed)
 */
export async function humanAmountToUsd(humanAmount: number, tokenMint: string): Promise<number | null> {
  const pricePerToken = await getUsdPricePerToken(tokenMint);
  if (pricePerToken === null) return null;
  return humanAmount * pricePerToken;
}

/**
 * Synchronous lookup — returns USD value using only a cached (real) price.
 * Safe to call from synchronous contexts; will not fetch fresh prices and will
 * NEVER return a hardcoded guess. Returns null when no valid cached price exists,
 * which callers must treat as "unknown", not zero.
 */
export function humanAmountToUsdSync(humanAmount: number, tokenMint: string): number | null {
  const staticPrice = getStaticUsdPrice(tokenMint);
  if (staticPrice !== null) return humanAmount * staticPrice;

  const cached = priceCache.get(tokenMint);
  if (cached && cached.expiresAt > Date.now()) {
    return humanAmount * cached.usdPerToken;
  }
  return null;
}

/**
 * Warms the USD price cache for a list of mints one at a time, with a small delay
 * between each Jupiter quote, instead of firing them all simultaneously. Calls
 * `onPrice` as each result resolves so callers can update UI state incrementally.
 *
 * Returns a cancel function — call it to stop the sweep promptly (component unmount,
 * a new sweep starting, etc.) so quotes don't keep firing after they're no longer needed.
 */
export function warmUsdPrices(
  mints: string[],
  onPrice?: (mint: string, price: number | null) => void,
  delayMs = 300,
): () => void {
  let cancelled = false;

  (async () => {
    for (const mint of mints) {
      if (cancelled) return;
      let price: number | null;
      try {
        price = await getUsdPricePerToken(mint);
      } catch {
        price = null;
      }
      if (cancelled) return;
      onPrice?.(mint, price);
      if (cancelled) return;
      if (delayMs > 0) {
        await new Promise<void>((resolve) => setTimeout(resolve, delayMs));
      }
    }
  })();

  return () => {
    cancelled = true;
  };
}
